import { supabaseFetch } from './core';
import { devLog } from '../log';

// ============================================
// COACH - ATHLETES
// ============================================

export const getCoachAthletes = async (coachId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getCoachAthletes called for coachId:', coachId);

  try {
    // Prima query: prende le relazioni coach-athlete
    const relationships = await supabaseFetch(
      `/coach_athletes?coach_id=eq.${coachId}&select=id,created_at,athlete_id`
    );

    /*#__PURE__*/ devLog('💾 [DATABASE] Relationships found:', relationships?.length || 0);

    if (!relationships || relationships.length === 0) {
      /*#__PURE__*/ devLog('💾 [DATABASE] No athletes found for this coach');
      return [];
    }

    // Seconda query: prende i dati dei profili degli atleti
    const athleteIds = relationships.map(r => r.athlete_id).join(',');
    const athletes = await supabaseFetch(
      `/profiles?id=in.(${athleteIds})&select=id,name,avatar_url`
    );

    /*#__PURE__*/ devLog('💾 [DATABASE] Athletes profiles fetched:', athletes?.length || 0);

    // Combina i risultati
    const result = relationships.map(rel => ({
      id: rel.id,
      created_at: rel.created_at,
      athlete: athletes.find(a => a.id === rel.athlete_id) || { id: rel.athlete_id, name: 'Atleta', avatar_url: null }
    }));

    /*#__PURE__*/ devLog('✅ [DATABASE] getCoachAthletes successful, returning', result.length, 'athletes');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getCoachAthletes error:', error);
    console.error('❌ [DATABASE] Error message:', error.message);

    // Ritorna array vuoto invece di throw per non bloccare la dashboard
    console.warn('⚠️ [DATABASE] Returning empty athletes list');
    return [];
  }
};

export const addAthleteToCoach = async (coachId, athleteEmail) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] addAthleteToCoach called');
  /*#__PURE__*/ devLog('💾 [DATABASE] coachId:', coachId);
  /*#__PURE__*/ devLog('💾 [DATABASE] athleteEmail:', athleteEmail);

  try {
    const normalizedEmail = athleteEmail.trim().toLowerCase();
    let athlete = null;

    // Strategy 1: Try RPC function (searches auth.users - always has email)
    try {
      /*#__PURE__*/ devLog('💾 [DATABASE] Trying RPC find_athlete_by_email...');
      const rpcResult = await supabaseFetch('/rpc/find_athlete_by_email', {
        method: 'POST',
        body: JSON.stringify({ search_email: normalizedEmail }),
      });
      if (rpcResult && rpcResult.length > 0) {
        athlete = rpcResult[0];
        /*#__PURE__*/ devLog('💾 [DATABASE] Found athlete via RPC:', athlete.id);
      }
    } catch (rpcError) {
      console.warn('💾 [DATABASE] RPC not available, falling back to profiles search:', rpcError.message);
    }

    // Strategy 2: Fallback - search profiles.email directly
    if (!athlete) {
      /*#__PURE__*/ devLog('💾 [DATABASE] Trying profiles.email search...');
      const athletes = await supabaseFetch(`/profiles?email=ilike.${encodeURIComponent(normalizedEmail)}&select=id,role,email`);
      if (athletes && athletes.length > 0) {
        athlete = athletes[0];
        /*#__PURE__*/ devLog('💾 [DATABASE] Found athlete via profiles:', athlete.id);
      }
    }

    if (!athlete) {
      throw new Error('Atleta non trovato. Verifica che l\'email sia corretta e che l\'atleta si sia registrato.');
    }

    if (athlete.id === coachId) {
      throw new Error('Non puoi aggiungere te stesso come atleta.');
    }

    /*#__PURE__*/ devLog('💾 [DATABASE] Athlete found:', athlete.id);

    // Pre-check: relazione già esistente → nessun errore tecnico
    const existing = await supabaseFetch(
      `/coach_athletes?coach_id=eq.${coachId}&athlete_id=eq.${athlete.id}&select=id`
    );
    if (existing && existing.length > 0) {
      /*#__PURE__*/ devLog('ℹ️ [DATABASE] Athlete already linked');
      return { alreadyLinked: true, athleteId: athlete.id };
    }

    // Crea la relazione (con fallback difensivo sul vincolo unique)
    try {
      const data = await supabaseFetch('/coach_athletes', {
        method: 'POST',
        body: JSON.stringify({ coach_id: coachId, athlete_id: athlete.id }),
      });
      /*#__PURE__*/ devLog('✅ [DATABASE] addAthleteToCoach successful');
      return Array.isArray(data) ? data[0] : data;
    } catch (insertErr) {
      const m = (insertErr.message || '').toLowerCase();
      if (m.includes('duplicate') || m.includes('coach_athletes_coach_id_athlete_id')) {
        /*#__PURE__*/ devLog('ℹ️ [DATABASE] Duplicate constraint → already linked');
        return { alreadyLinked: true, athleteId: athlete.id };
      }
      throw insertErr;
    }
  } catch (error) {
    console.error('❌ [DATABASE] addAthleteToCoach error:', error);
    throw error;
  }
};

export const removeAthleteFromCoach = async (relationshipId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] removeAthleteFromCoach called');
  /*#__PURE__*/ devLog('💾 [DATABASE] relationshipId:', relationshipId);

  try {
    await supabaseFetch(`/coach_athletes?id=eq.${relationshipId}`, {
      method: 'DELETE',
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] removeAthleteFromCoach successful');
  } catch (error) {
    console.error('❌ [DATABASE] removeAthleteFromCoach error:', error);
    throw error;
  }
};

/**
 * Get coach with their athletes count
 * @param {string} coachId - Coach UUID
 * @returns {Object} - Coach profile with athlete count
 */
export const getCoachWithAthletesCount = async (coachId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getCoachWithAthletesCount for:', coachId);

    const [profile, athletes] = await Promise.all([
      supabaseFetch(`/profiles?id=eq.${coachId}&select=*`),
      supabaseFetch(`/coach_athletes?coach_id=eq.${coachId}&select=id`)
    ]);

    if (!profile || profile.length === 0) {
      return null;
    }

    return {
      ...profile[0],
      athleteCount: athletes?.length || 0
    };
  } catch (error) {
    console.error('❌ [DATABASE] getCoachWithAthletesCount error:', error);
    return null;
  }
};

/**
 * Get athlete with their coaches
 * @param {string} athleteId - Athlete UUID
 * @returns {Object} - Athlete profile with coaches
 */
export const getAthleteWithCoaches = async (athleteId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAthleteWithCoaches for:', athleteId);

    const [profile, relationships] = await Promise.all([
      supabaseFetch(`/profiles?id=eq.${athleteId}&select=*`),
      supabaseFetch(`/coach_athletes?athlete_id=eq.${athleteId}&select=coach_id`)
    ]);

    if (!profile || profile.length === 0) {
      return null;
    }

    let coaches = [];
    if (relationships && relationships.length > 0) {
      const coachIds = relationships.map(r => r.coach_id).join(',');
      coaches = await supabaseFetch(`/profiles?id=in.(${coachIds})&select=id,name,email`);
    }

    return {
      ...profile[0],
      coaches: coaches || []
    };
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteWithCoaches error:', error);
    return null;
  }
};

/**
 * Get coach athletes with all their data in batch (optimized for dashboard)
 * @param {string} coachId - Coach UUID
 * @returns {Array} - Athletes with program, stats, and last workout
 */
export const getCoachAthletesWithStats = async (coachId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getCoachAthletesWithStats (batch optimized) called for coachId:', coachId);

  try {
    // Step 1: Get athlete relationships and profiles
    const relationships = await supabaseFetch(
      `/coach_athletes?coach_id=eq.${coachId}&select=id,created_at,athlete_id`
    );

    if (!relationships || relationships.length === 0) {
      /*#__PURE__*/ devLog('💾 [DATABASE] No athletes found for this coach');
      return [];
    }

    const athleteIds = relationships.map(r => r.athlete_id);
    const athleteIdsStr = athleteIds.join(',');

    // Step 2: Batch fetch athlete profiles
    const athletesPromise = supabaseFetch(
      `/profiles?id=in.(${athleteIdsStr})&select=id,name,avatar_url`
    );

    // Step 3: Batch fetch active programs (via athlete_programs with is_active=true)
    const activeProgramsPromise = supabaseFetch(
      `/athlete_programs?athlete_id=in.(${athleteIdsStr})&is_active=eq.true&select=athlete_id,program:program_id(id,name)`
    );

    // Step 4: Batch fetch workout stats (total workouts per athlete)
    const workoutStatsPromise = supabaseFetch(
      `/workout_logs?athlete_id=in.(${athleteIdsStr})&select=athlete_id`
    );

    // Step 5: Batch fetch last workout for each athlete
    // Fetch recent workouts and filter client-side
    const recentWorkoutsPromise = supabaseFetch(
      `/workout_logs?athlete_id=in.(${athleteIdsStr})&select=id,athlete_id,created_at,duration_minutes&order=created_at.desc&limit=100`
    );

    // Execute all fetches in parallel. Le stat sono ACCESSORIE: se una fallisce
    // (es. RLS/errore transitorio) NON deve svuotare la lista atleti.
    const [athletes, activePrograms, workoutStats, recentWorkouts] = await Promise.all([
      athletesPromise.catch(() => []),
      activeProgramsPromise.catch(() => []),
      workoutStatsPromise.catch(() => []),
      recentWorkoutsPromise.catch(() => [])
    ]);

    /*#__PURE__*/ devLog('💾 [DATABASE] Batch fetch complete - athletes:', athletes?.length, 'programs:', activePrograms?.length, 'stats:', workoutStats?.length, 'workouts:', recentWorkouts?.length);

    // Build maps for quick lookup
    const athleteMap = new Map(athletes.map(a => [a.id, a]));
    const activeProgramMap = new Map(activePrograms.map(ap => [ap.athlete_id, ap.program]));

    // Count workouts per athlete
    const workoutCountMap = new Map();
    workoutStats.forEach(ws => {
      workoutCountMap.set(ws.athlete_id, (workoutCountMap.get(ws.athlete_id) || 0) + 1);
    });

    // Get last workout per athlete
    const lastWorkoutMap = new Map();
    recentWorkouts.forEach(wl => {
      if (!lastWorkoutMap.has(wl.athlete_id)) {
        lastWorkoutMap.set(wl.athlete_id, wl);
      }
    });

    // Calculate current streak per athlete (consecutive days with workouts)
    const streakMap = new Map();
    athleteIds.forEach(athleteId => {
      const athleteWorkouts = recentWorkouts
        .filter(wl => wl.athlete_id === athleteId)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      let streak = 0;
      if (athleteWorkouts.length > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let currentDate = today;

        for (const workout of athleteWorkouts) {
          const workoutDate = new Date(workout.created_at);
          workoutDate.setHours(0, 0, 0, 0);

          const diffDays = Math.floor((currentDate - workoutDate) / (1000 * 60 * 60 * 24));

          if (diffDays <= 1) {
            streak++;
            currentDate = workoutDate;
          } else {
            break;
          }
        }
      }
      streakMap.set(athleteId, streak);
    });

    // Combine all data
    const result = relationships.map(rel => {
      const athlete = athleteMap.get(rel.athlete_id) || { id: rel.athlete_id, name: 'Atleta', avatar_url: null };
      const activeProgram = activeProgramMap.get(rel.athlete_id);
      const workoutCount = workoutCountMap.get(rel.athlete_id) || 0;
      const lastWorkout = lastWorkoutMap.get(rel.athlete_id) || null;
      const currentStreak = streakMap.get(rel.athlete_id) || 0;

      return {
        id: rel.id,
        athleteId: athlete.id,
        name: athlete.name,
        avatar_url: athlete.avatar_url,
        program: activeProgram?.name || null,
        stats: {
          totalWorkouts: workoutCount,
          currentStreak: currentStreak
        },
        lastWorkout: lastWorkout
      };
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] getCoachAthletesWithStats successful, returning', result.length, 'athletes with full data');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getCoachAthletesWithStats error:', error);
    console.error('❌ [DATABASE] Error message:', error.message);

    // Ritorna array vuoto invece di throw per non bloccare la dashboard
    console.warn('⚠️ [DATABASE] Returning empty athletes list');
    return [];
  }
};
